'use client'

import { m, useMotionValue, useSpring, useTransform } from 'framer-motion'
import {
  ArrowUpRight,
  Check,
  Code2,
  Cpu,
  type LucideIcon,
  MessageSquare,
  Rocket,
  Sparkles,
} from 'lucide-react'
import Link from 'next/link'
import { type CSSProperties, useRef, useState } from 'react'

import { getDirectionTheme } from '@/lib/directionTheme'
import type { ServiceDTO } from '@/types/services'

const ICON_BY_SLUG: Record<string, LucideIcon> = {
  development: Code2,
  'ai-agents': Cpu,
  design: Sparkles,
  smm: MessageSquare,
  marketing: Rocket,
}

type HomeServiceCard = {
  id: string
  number: string
  title: string
  subtitle: string
  description: string
  features: string[]
  icon: LucideIcon
  gradient: string
  accentRgb: string
  href: string
}

function ServiceCard({
  service,
  index,
  className,
}: {
  service: HomeServiceCard
  index: number
  className?: string
}) {
  const [isHovered, setIsHovered] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const x = useMotionValue(0)
  const y = useMotionValue(0)

  const mouseXSpring = useSpring(x)
  const mouseYSpring = useSpring(y)

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ['8deg', '-8deg'])
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ['-8deg', '8deg'])
  const cardStyle = {
    rotateX,
    rotateY,
    transformStyle: 'preserve-3d',
    '--accent-rgb': service.accentRgb,
  } as CSSProperties & Record<'--accent-rgb', string>

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current) return
    const rect = ref.current.getBoundingClientRect()
    const width = rect.width
    const height = rect.height
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top
    const xPct = mouseX / width - 0.5
    const yPct = mouseY / height - 0.5
    x.set(xPct)
    y.set(yPct)
  }

  const handleMouseLeave = () => {
    x.set(0)
    y.set(0)
    setIsHovered(false)
  }

  return (
    <m.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.15, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      style={cardStyle}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      className={`group perspective-1000 relative ${className || ''}`}
    >
      <Link href={service.href}>
        <div
          className={`relative h-full overflow-hidden rounded-[24px] p-6 [box-shadow:0_0_0px_transparent] transition-all duration-500 group-hover:[box-shadow:0_30px_90px_rgb(var(--accent-rgb)_/_0.28)] lg:p-8`}
        >
          <div className="absolute inset-0 rounded-[32px] bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-xl" />
          <div
            className={`absolute inset-0 rounded-[32px] border transition-colors duration-500 ${isHovered ? 'border-white/30' : 'border-white/10'}`}
          />
          <div
            className={`absolute -inset-[100px] bg-gradient-to-br ${service.gradient} rounded-full blur-[120px] transition-opacity duration-700 ${isHovered ? 'opacity-20' : 'opacity-0'}`}
          />
          <div className="absolute inset-x-0 top-0 h-px rounded-t-[32px] bg-gradient-to-r from-transparent via-white/30 to-transparent" />

          <div className="relative z-10 flex h-full flex-col">
            <div className="mb-6 flex items-start justify-between">
              <div
                className={`h-16 w-16 rounded-2xl bg-gradient-to-br ${service.gradient} p-[2px] transition-transform duration-500 ${isHovered ? 'scale-110 rotate-3' : ''}`}
              >
                <div className="flex h-full w-full items-center justify-center rounded-2xl bg-[#0a0f1a]/90 backdrop-blur-xl">
                  <service.icon className="h-8 w-8 text-white" strokeWidth={1.5} />
                </div>
              </div>
              <span
                className={`bg-gradient-to-br text-6xl font-bold ${service.gradient} bg-clip-text text-transparent opacity-30`}
              >
                {service.number}
              </span>
            </div>

            <p className="home-caption mb-2 text-gray-500">{service.subtitle}</p>
            <h3 className="home-card-title mb-4">{service.title}</h3>
            <p className="home-subnote mb-6 text-gray-400">{service.description}</p>

            <div className="mt-auto mb-6 grid grid-cols-2 gap-2">
              {service.features.map((feature, i) => (
                <div key={i} className="home-body flex items-center gap-2 text-gray-400">
                  <Check
                    className={`h-4 w-4 bg-gradient-to-br ${service.gradient} bg-clip-text`}
                    style={{ color: 'currentColor' }}
                  />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between border-t border-white/5 pt-6">
              <span className="home-meta text-gray-500 transition-colors duration-300 group-hover:text-white">
                Подробнее об услуге
              </span>
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-full transition-all duration-500 ${
                  isHovered
                    ? `bg-gradient-to-br ${service.gradient}`
                    : 'border border-white/10 bg-white/5'
                }`}
              >
                <ArrowUpRight
                  className={`h-5 w-5 transition-all duration-300 ${
                    isHovered ? 'translate-x-0.5 -translate-y-0.5 text-white' : 'text-white/70'
                  }`}
                />
              </div>
            </div>
          </div>
        </div>
      </Link>
    </m.div>
  )
}

export default function DirectionsClient({ services }: { services: ServiceDTO[] }) {
  const cards: HomeServiceCard[] = (services || [])
    .filter((s) => s.slug !== 'design')
    .map((s, idx) => {
      const Icon = ICON_BY_SLUG[s.slug] || Code2
      const theme = getDirectionTheme(s.slug)
      const gradient = theme.homeGradient || s.gradient || 'from-blue-600 via-blue-500 to-cyan-400'
      const accentRgb = theme.accentRgb
      const subtitle = s.subtitle || 'Digital направление'
      const features = (s.stack || []).slice(0, 4)

      let description = s.description
      if (s.slug === 'development') {
        description =
          'Ваш цифровой прорыв. В мире, где сайт — это лицо бизнеса, мы создаем не просто веб-страницы, а мощные инструменты продаж и роста. Интерактивные интерфейсы, молниеносная загрузка и архитектура, готовая к любым нагрузкам.'
      } else if (s.slug === 'ai-agents') {
        description =
          'Эволюция вашего сервиса. Искусственный интеллект, который берет на себя рутину, продает 24/7 и общается с клиентами лучше живого оператора. Автоматизация, которая экономит миллионы и масштабирует бизнес.'
      } else if (s.slug === 'smm') {
        description =
          'Голос, который слышат. Мы не просто ведем соцсети — мы строим культуру вокруг вашего бренда. Визуальный сторителлинг, вирусные охваты и комьюнити, которое становится вашей главной силой продаж.'
      } else if (s.slug === 'marketing') {
        description =
          'Трафик ради трафика — деньги на ветер. Мы приводим клиентов, которые покупают. Контекст, таргет, SEO, инфлюенс-маркетинг — используем все каналы, которые работают для вашего бизнеса. Сквозная аналитика, полная прозрачность и рост прибыли, который можно посчитать.'
      }

      return {
        id: s.slug,
        number: String(idx + 1).padStart(2, '0'),
        title: s.title,
        subtitle,
        description,
        features,
        icon: Icon,
        gradient,
        accentRgb,
        href: `/directions?tab=${s.slug}`,
      }
    })

  return (
    <section className="home-section relative overflow-hidden">
      <div className="home-container relative z-10">
        <div className="relative mb-12 overflow-hidden rounded-[40px] border border-white/10 bg-white/[0.02] backdrop-blur-xl">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-pink-600/5 to-blue-600/10 opacity-60" />
            <div className="absolute -top-32 -left-32 h-[520px] w-[520px] rounded-full bg-purple-500/10 blur-[120px]" />
            <div className="absolute -right-32 -bottom-32 h-[520px] w-[520px] rounded-full bg-blue-500/10 blur-[120px]" />
          </div>

          <div className="relative p-8 md:p-10 lg:p-14">
            <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl">
                <m.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2"
                >
                  <span className="h-2 w-2 rounded-full bg-purple-500" />
                  <span className="home-caption text-gray-400">Наши услуги</span>
                </m.div>

                <m.h2
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="home-title leading-[1.03]"
                >
                  Полный спектр
                  <br />
                  <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent">
                    цифровых услуг
                  </span>
                </m.h2>
              </div>

              <m.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="home-lead max-w-xl text-gray-300/80 lg:text-right"
              >
                От идеи до масштабирования — закрываем весь цикл. Одна команда, без посредников и
                подрядчиков.
              </m.p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
          {cards.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  )
}
